/**
 * Password Reset Page
 * Request password reset or confirm with token
 */

import { useState, useEffect } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { api } from '../services/api'
import { validateEmail, validatePassword } from '../utils/validators'

export default function PasswordResetPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token')

  const [mode, setMode] = useState<'request' | 'confirm'>(token ? 'confirm' : 'request')
  const [isLoading, setIsLoading] = useState(false)
  const [success, setSuccess] = useState(false)

  // Request form data
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState('')

  // Confirm form data
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: '',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [apiError, setApiError] = useState('')

  useEffect(() => {
    setMode(token ? 'confirm' : 'request')
  }, [token])

  const handleRequestSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setApiError('')
    setEmailError('')

    const error = validateEmail(email)
    if (error) {
      setEmailError(error)
      return
    }

    setIsLoading(true)
    try {
      await api.post('/auth/reset-password', { email })
      setSuccess(true)
    } catch (error) {
      if (error instanceof Error) {
        setApiError(error.message)
      } else {
        setApiError('發送失敗，請稍後再試')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleConfirmSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setApiError('')
    setErrors({})

    const newErrors: Record<string, string> = {}

    const passwordError = validatePassword(formData.newPassword)
    if (passwordError) newErrors.newPassword = passwordError

    if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = '密碼不一致'
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setIsLoading(true)
    try {
      await api.post('/auth/reset-password/confirm', {
        token,
        new_password: formData.newPassword,
      })
      setSuccess(true)
      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/login')
      }, 2000)
    } catch (error) {
      if (error instanceof Error) {
        setApiError(error.message)
      } else {
        setApiError('重設失敗，請稍後再試')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
    if (apiError) {
      setApiError('')
    }
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h1 style={styles.title}>CyberLoan.ai</h1>
          <p style={styles.subtitle}>
            {mode === 'request' ? '重設密碼' : '設定新密碼'}
          </p>
        </div>

        {success ? (
          <div style={styles.successBox}>
            <span style={styles.successIcon}>✓</span>
            <div>
              {mode === 'request' ? (
                <>
                  <p style={styles.successTitle}>郵件已發送！</p>
                  <p style={styles.successText}>
                    如果該郵箱存在，我們已發送重設密碼連結到您的信箱。
                  </p>
                  <p style={styles.successText}>請檢查您的電子郵件。</p>
                </>
              ) : (
                <>
                  <p style={styles.successTitle}>密碼重設成功！</p>
                  <p style={styles.successText}>您的密碼已成功更新。</p>
                  <p style={styles.successText}>即將跳轉到登入頁面...</p>
                </>
              )}
            </div>
          </div>
        ) : mode === 'request' ? (
          <form onSubmit={handleRequestSubmit} style={styles.form}>
            {apiError && (
              <div style={styles.errorBox}>
                <span style={styles.errorIcon}>⚠️</span>
                <span>{apiError}</span>
              </div>
            )}

            <div style={styles.infoBox}>
              <p style={styles.infoText}>
                請輸入您的電子郵件地址，我們將發送重設密碼連結給您。
              </p>
            </div>

            <div style={styles.formGroup}>
              <label htmlFor="email" style={styles.label}>
                電子郵件
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  setEmailError('')
                  setApiError('')
                }}
                style={{
                  ...styles.input,
                  ...(emailError ? styles.inputError : {}),
                }}
                placeholder="your@email.com"
                autoComplete="email"
                disabled={isLoading}
              />
              {emailError && <span style={styles.errorText}>{emailError}</span>}
            </div>

            <button type="submit" style={styles.submitButton} disabled={isLoading}>
              {isLoading ? '發送中...' : '發送重設連結'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleConfirmSubmit} style={styles.form}>
            {apiError && (
              <div style={styles.errorBox}>
                <span style={styles.errorIcon}>⚠️</span>
                <span>{apiError}</span>
              </div>
            )}

            <div style={styles.infoBox}>
              <p style={styles.infoText}>請設定您的新密碼。</p>
            </div>

            <div style={styles.formGroup}>
              <label htmlFor="newPassword" style={styles.label}>
                新密碼
              </label>
              <input
                type="password"
                id="newPassword"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
                style={{
                  ...styles.input,
                  ...(errors.newPassword ? styles.inputError : {}),
                }}
                placeholder="••••••••"
                autoComplete="new-password"
                disabled={isLoading}
              />
              {errors.newPassword && <span style={styles.errorText}>{errors.newPassword}</span>}
              <div style={styles.passwordHint}>
                <small style={styles.hintText}>
                  至少 8 字元，包含大小寫字母和數字
                </small>
              </div>
            </div>

            <div style={styles.formGroup}>
              <label htmlFor="confirmPassword" style={styles.label}>
                確認新密碼
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                style={{
                  ...styles.input,
                  ...(errors.confirmPassword ? styles.inputError : {}),
                }}
                placeholder="••••••••"
                autoComplete="new-password"
                disabled={isLoading}
              />
              {errors.confirmPassword && <span style={styles.errorText}>{errors.confirmPassword}</span>}
            </div>

            <button type="submit" style={styles.submitButton} disabled={isLoading}>
              {isLoading ? '重設中...' : '重設密碼'}
            </button>
          </form>
        )}

        <div style={styles.footer}>
          <Link to="/login" style={styles.link}>
            ← 返回登入
          </Link>
        </div>
      </div>
    </div>
  )
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f5f5f5',
    padding: '20px',
  },
  card: {
    width: '100%',
    maxWidth: '450px',
    backgroundColor: 'white',
    borderRadius: '10px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    padding: '40px',
  },
  header: {
    textAlign: 'center',
    marginBottom: '30px',
  },
  title: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#2563eb',
    marginBottom: '8px',
  },
  subtitle: {
    fontSize: '16px',
    color: '#666',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#333',
  },
  input: {
    padding: '12px',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '5px',
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  inputError: {
    borderColor: '#dc2626',
  },
  errorText: {
    fontSize: '12px',
    color: '#dc2626',
  },
  errorBox: {
    padding: '12px',
    backgroundColor: '#fee2e2',
    borderRadius: '5px',
    color: '#dc2626',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  errorIcon: {
    fontSize: '16px',
  },
  successBox: {
    padding: '20px',
    backgroundColor: '#dcfce7',
    borderRadius: '5px',
    color: '#15803d',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    marginBottom: '20px',
  },
  successIcon: {
    fontSize: '24px',
    fontWeight: 'bold',
  },
  successTitle: {
    fontSize: '16px',
    fontWeight: 'bold',
    marginBottom: '8px',
  },
  successText: {
    fontSize: '14px',
    marginBottom: '4px',
  },
  infoBox: {
    padding: '12px',
    backgroundColor: '#e0f2fe',
    borderRadius: '5px',
    color: '#0369a1',
    fontSize: '14px',
  },
  infoText: {
    margin: 0,
  },
  passwordHint: {
    marginTop: '4px',
  },
  hintText: {
    fontSize: '12px',
    color: '#666',
  },
  link: {
    color: '#2563eb',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: '500',
  },
  submitButton: {
    padding: '12px',
    backgroundColor: '#2563eb',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  footer: {
    marginTop: '30px',
    textAlign: 'center',
    fontSize: '14px',
  },
}
